import PageHeader from "@/components/page-header";
import { BulkPaymentDashboardFilterSummary } from "@/components/registrant/bulk-payment-dashboard-filter-summary";
import { CheckPaymentSuccessDialog } from "@/components/registrant/check-payment-success-dialog";
import { ContextHelpButton } from "@/components/registrant/help/context-help-button";
import { FeeAmountInput } from "@/components/registrant/license-form/fee-amount-input";
import { FeesPaymentScreenQaBlock } from "@/components/registrant/license-form/fees-payment-screen-qa";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/sonner";
import {
  type BulkPaymentFeesItem,
  createBulkCheckPayment,
  createBulkInvoicePayment,
  createBulkPendingPaymentRecords,
  getBulkPaymentFees,
  makeBulkLicensePayment,
} from "@/hera-api/bulk-payment-api";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { useBulkPaymentDashboardFilters } from "@/hooks/use-bulk-payment-dashboard-filters";
import { useRegistrantOrgPermissions } from "@/hooks/use-registrant-org-permissions";
import { sortLicenseTypesByDisplayOrder } from "@/lib/license-type-utils";
import {
  BULK_PAYMENT_FEES_QUERY_KEY,
  bulkPaymentFeesQueryOptions,
  invalidateRegistrantDashboardLists,
} from "@/lib/registrant-dashboard-query-invalidation";
import {
  formatAnswerValue,
  formatCurrency,
  formatQuestionLabelForFeesPayment,
  parseDateAsLocal,
  roundToTwoDecimals,
} from "@/lib/utils";
import { SESSION_KEYS, sessionStorage } from "@/types/session-storage";
import {
  type QueryClient,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import {
  Banknote,
  ChevronDown,
  ChevronUp,
  CreditCard,
  FileText,
  Landmark,
  Loader2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

export type EditedPeriod = {
  selected: boolean;
  fee: string;
  late_fee: string;
};

export type EditedItemState = {
  includedInPayment: boolean;
  periods: EditedPeriod[];
  inspection_fee: string;
};

/** Distinct license types among items with a balance due (for type filter). */
function uniqueLicenseTypesWithDue(items: BulkPaymentFeesItem[]) {
  const map = new Map<
    string,
    { id: string; name: string; display_order: number }
  >();
  for (const item of items) {
    if (!map.has(item.license_type_id)) {
      map.set(item.license_type_id, {
        id: item.license_type_id,
        name: item.license_type_name,
        display_order: item.license_type_display_order,
      });
    }
  }
  return sortLicenseTypesByDisplayOrder([...map.values()]);
}

function buildInitialEditedItems(
  items: BulkPaymentFeesItem[],
): Record<string, EditedItemState> {
  const acc: Record<string, EditedItemState> = {};
  for (const item of items) {
    acc[item.license_id] = {
      includedInPayment: true,
      periods: item.fees.map((f) => ({
        selected: true,
        fee: roundToTwoDecimals(f.registration_fee),
        late_fee: roundToTwoDecimals(f.late_fee),
      })),
      inspection_fee: item.inspection_fee
        ? roundToTwoDecimals(item.inspection_fee)
        : "0.00",
    };
  }
  return acc;
}

function companyNameFromContactAnswer(raw: unknown): string {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const record = raw as Record<string, unknown>;
    const company = record.companyName ?? record.company_name;
    if (typeof company === "string" && company.trim()) {
      return company.trim();
    }
    const name = record.name;
    if (typeof name === "string" && name.trim()) {
      return name.trim();
    }
  }
  const formatted = formatAnswerValue(raw);
  return formatted === "Not answered" ? "" : formatted;
}

function isAmsFeesQuestionLabel(question: string): boolean {
  const normalized = formatQuestionLabelForFeesPayment(question)
    .trim()
    .toUpperCase();
  return normalized === "AMS" || normalized.includes("ASSET MANAGER");
}

/** Prefer API AMS, then fees-screen answers labeled AMS / Asset Manager. */
function getItemAmsLabel(item: BulkPaymentFeesItem): string {
  const fromApi = item.ams_company_name?.trim();
  if (fromApi) return fromApi;

  const questions = item.fees_payment_screen_questions ?? [];
  const answers = item.answers_to_additional_questions ?? {};
  for (const q of questions) {
    if (!isAmsFeesQuestionLabel(q.question)) continue;
    return companyNameFromContactAnswer(answers[q.id]);
  }
  return "";
}

function uniqueAmsLabels(items: BulkPaymentFeesItem[]): string[] {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const item of items) {
    const label = getItemAmsLabel(item);
    if (!label || seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels.sort((a, b) => a.localeCompare(b));
}

function itemTotalFromEdited(edited: EditedItemState): number {
  let total = 0;
  for (const p of edited.periods) {
    if (p.selected) {
      total += Number.parseFloat(p.fee) || 0;
      total += Number.parseFloat(p.late_fee) || 0;
    }
  }
  total += Number.parseFloat(edited.inspection_fee) || 0;
  return total;
}

// Stripe fee constants (matching backend and periods-and-fees)
const ACH_FEE_RATE = 0.008;
const MAX_ACH_FEE_CENTS = 500;
const CREDIT_CARD_FEE_RATE = 0.029;
const CREDIT_CARD_FIXED_FEE_CENTS = 30;

function calculateStripeFeeCents(
  method: "card" | "us_bank_account",
  subtotalDollars: number,
): number {
  if (subtotalDollars <= 0) return 0;
  const subtotalInCents = Math.floor(subtotalDollars * 100);
  if (method === "us_bank_account") {
    const fee = Math.floor(
      (subtotalInCents * ACH_FEE_RATE) / (1 - ACH_FEE_RATE),
    );
    return Math.min(fee, MAX_ACH_FEE_CENTS);
  }
  return Math.floor(
    (subtotalInCents * CREDIT_CARD_FEE_RATE + CREDIT_CARD_FIXED_FEE_CENTS) /
      (1 - CREDIT_CARD_FEE_RATE),
  );
}

/** Apportion totalFeeCents across items by subtotal share. Each share is rounded to nearest cent; the last item with positive subtotal gets the remainder so the sum equals totalFeeCents exactly. Items with zero subtotal get 0. */
function apportionFeeCents(
  totalFeeCents: number,
  subtotals: number[],
): number[] {
  if (subtotals.length === 0 || totalFeeCents <= 0) {
    return subtotals.map(() => 0);
  }
  const totalSub = subtotals.reduce((a, b) => a + b, 0);
  if (totalSub <= 0) return subtotals.map(() => 0);

  const result = subtotals.map(() => 0);
  const indicesWithShare = subtotals
    .map((s, i) => (s > 0 ? i : -1))
    .filter((i) => i >= 0);
  if (indicesWithShare.length === 0) return result;

  let assigned = 0;
  for (let j = 0; j < indicesWithShare.length - 1; j++) {
    const i = indicesWithShare[j];
    const cents = Math.round((totalFeeCents * subtotals[i]) / totalSub);
    result[i] = cents;
    assigned += cents;
  }
  result[indicesWithShare[indicesWithShare.length - 1]] =
    totalFeeCents - assigned;
  return result;
}

async function syncRegistrantDashboardCachesAfterBulkPayment(
  queryClient: QueryClient,
) {
  await invalidateRegistrantDashboardLists(queryClient);
}

export const Route = createFileRoute("/registrant/bulk-payment")({
  component: BulkPaymentScreen,
});

export function BulkPaymentScreen() {
  const queryClient = useQueryClient();
  const { isAuthSetupComplete } = useAuthQuerySetup();
  const { canMakePayments, account: registrantAccount } =
    useRegistrantOrgPermissions();
  const [paymentMethod, setPaymentMethod] = useState<
    "card" | "us_bank_account" | "check" | "invoice"
  >("card");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [checkPaymentSuccess, setCheckPaymentSuccess] = useState<{
    displayId: string;
    checkMailingAddress: string | null;
    amountToPay: string;
    variant: "check" | "invoice";
  } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (registrantAccount && !canMakePayments) {
      void navigate({ to: "/registrant/dashboard" });
    }
  }, [canMakePayments, navigate, registrantAccount]);

  const {
    filters: dashboardFilters,
    hasFilters: hasDashboardFilters,
    clearFilters: clearDashboardFilters,
  } = useBulkPaymentDashboardFilters();

  const { data, isLoading, error } = useQuery({
    queryKey: [...BULK_PAYMENT_FEES_QUERY_KEY, dashboardFilters],
    queryFn: () => getBulkPaymentFees(dashboardFilters),
    enabled: isAuthSetupComplete,
    ...bulkPaymentFeesQueryOptions,
  });

  const [editedItems, setEditedItems] = useState<
    Record<string, EditedItemState>
  >({});
  const [selectedLicenseTypeId, setSelectedLicenseTypeId] = useState<
    string | null
  >(null);
  const [selectedAmsCompanyName, setSelectedAmsCompanyName] = useState<
    string | null
  >(dashboardFilters.amsCompanyName);
  const [submitDisplayItems, setSubmitDisplayItems] = useState<
    BulkPaymentFeesItem[] | null
  >(null);

  const handleClearDashboardFilters = () => {
    setSelectedAmsCompanyName(null);
    setSelectedLicenseTypeId(null);
    clearDashboardFilters();
  };

  const displayItems = submitDisplayItems ?? data?.items ?? [];

  useEffect(() => {
    if (data?.items?.length && !isSubmitting) {
      setEditedItems(buildInitialEditedItems(data.items));
    }
  }, [data?.items, isSubmitting]);

  const licenseTypesWithDue = useMemo(
    () => uniqueLicenseTypesWithDue(displayItems),
    [displayItems],
  );
  const showLicenseTypePicker = licenseTypesWithDue.length > 1;
  const effectiveLicenseTypeId = useMemo(() => {
    if (!licenseTypesWithDue.length) return null;
    if (
      selectedLicenseTypeId &&
      licenseTypesWithDue.some((t) => t.id === selectedLicenseTypeId)
    ) {
      return selectedLicenseTypeId;
    }
    return licenseTypesWithDue[0].id;
  }, [licenseTypesWithDue, selectedLicenseTypeId]);

  const licenseTypeFilteredItems = useMemo(() => {
    if (!displayItems.length) return [];
    if (!showLicenseTypePicker) return displayItems;
    if (!effectiveLicenseTypeId) return [];
    return displayItems.filter(
      (i) => i.license_type_id === effectiveLicenseTypeId,
    );
  }, [displayItems, showLicenseTypePicker, effectiveLicenseTypeId]);

  const amsOptions = useMemo(
    () => uniqueAmsLabels(licenseTypeFilteredItems),
    [licenseTypeFilteredItems],
  );
  const showAmsPicker = amsOptions.length > 0;
  const effectiveAmsCompanyName = useMemo(() => {
    if (!amsOptions.length) return null;
    if (selectedAmsCompanyName && amsOptions.includes(selectedAmsCompanyName)) {
      return selectedAmsCompanyName;
    }
    if (amsOptions.length === 1) return amsOptions[0];
    return null;
  }, [amsOptions, selectedAmsCompanyName]);

  useEffect(() => {
    sessionStorage.set(
      SESSION_KEYS.REGISTRANT_DASHBOARD_SELECTED_AMS,
      effectiveAmsCompanyName,
    );
  }, [effectiveAmsCompanyName]);

  const visibleItems = useMemo(() => {
    if (!licenseTypeFilteredItems.length) return [];
    if (!effectiveAmsCompanyName) return licenseTypeFilteredItems;
    return licenseTypeFilteredItems.filter(
      (item) => getItemAmsLabel(item) === effectiveAmsCompanyName,
    );
  }, [effectiveAmsCompanyName, licenseTypeFilteredItems]);

  const { totalRegistrationFee, totalLateFee, totalInspectionFee, subtotal } =
    useMemo(() => {
      const sumVisibleFromEdited = () => {
        let reg = 0;
        let late = 0;
        let insp = 0;
        for (const item of visibleItems) {
          const edited = editedItems[item.license_id];
          if (edited?.includedInPayment === false) continue;
          if (edited) {
            for (const p of edited.periods) {
              if (p.selected) {
                reg += Number.parseFloat(p.fee) || 0;
                late += Number.parseFloat(p.late_fee) || 0;
              }
            }
            insp += Number.parseFloat(edited.inspection_fee) || 0;
          } else {
            for (const f of item.fees) {
              reg += Number.parseFloat(f.registration_fee) || 0;
              late += Number.parseFloat(f.late_fee) || 0;
            }
            insp += Number.parseFloat(item.inspection_fee ?? "0") || 0;
          }
        }
        return { reg, late, insp };
      };

      if (!visibleItems.length) {
        return {
          totalRegistrationFee: 0,
          totalLateFee: 0,
          totalInspectionFee: 0,
          subtotal: 0,
        };
      }

      if (Object.keys(editedItems).length > 0) {
        const { reg, late, insp } = sumVisibleFromEdited();
        return {
          totalRegistrationFee: reg,
          totalLateFee: late,
          totalInspectionFee: insp,
          subtotal: reg + late + insp,
        };
      }

      let reg = 0;
      let late = 0;
      let insp = 0;
      for (const item of visibleItems) {
        for (const f of item.fees) {
          reg += Number.parseFloat(f.registration_fee) || 0;
          late += Number.parseFloat(f.late_fee) || 0;
        }
        insp += Number.parseFloat(item.inspection_fee ?? "0") || 0;
      }
      return {
        totalRegistrationFee: reg,
        totalLateFee: late,
        totalInspectionFee: insp,
        subtotal: reg + late + insp,
      };
    }, [editedItems, visibleItems]);

  const subtotalsPerLicense = useMemo(() => {
    if (!visibleItems.length) return [];
    return visibleItems.map((item) => {
      const edited = editedItems[item.license_id];
      if (edited?.includedInPayment === false) return 0;
      if (!edited) {
        const pt = item.fees.reduce(
          (sum, f) =>
            sum +
            Number.parseFloat(f.registration_fee) +
            Number.parseFloat(f.late_fee),
          0,
        );
        const insp = Number.parseFloat(item.inspection_fee ?? "0") || 0;
        return pt + insp;
      }
      let total = 0;
      for (const p of edited.periods) {
        if (p.selected) {
          total += Number.parseFloat(p.fee) || 0;
          total += Number.parseFloat(p.late_fee) || 0;
        }
      }
      total += Number.parseFloat(edited.inspection_fee) || 0;
      return total;
    });
  }, [visibleItems, editedItems]);

  const totalProcessingFeeCents = useMemo(() => {
    if (paymentMethod === "check") {
      const firstFee = visibleItems[0]?.check_processing_fee;
      const totalCheckDollars = firstFee ? Number.parseFloat(firstFee) || 0 : 0;
      return Math.round(totalCheckDollars * 100);
    }
    if (paymentMethod === "invoice") {
      return 0;
    }
    return calculateStripeFeeCents(paymentMethod, subtotal);
  }, [paymentMethod, subtotal, visibleItems]);

  const processingFeeByLicenseId = useMemo(() => {
    if (!visibleItems.length) return {} as Record<string, number>;
    const centsPerItem = apportionFeeCents(
      totalProcessingFeeCents,
      subtotalsPerLicense,
    );
    const out: Record<string, number> = {};
    visibleItems.forEach((item, i) => {
      out[item.license_id] = (centsPerItem[i] ?? 0) / 100;
    });
    return out;
  }, [visibleItems, totalProcessingFeeCents, subtotalsPerLicense]);

  const processingFeeDollars = totalProcessingFeeCents / 100;
  const totalWithFee = subtotal + processingFeeDollars;

  const handleUpdateItem = (licenseId: string, next: EditedItemState) => {
    const stateToSet =
      itemTotalFromEdited(next) === 0
        ? { ...next, includedInPayment: false }
        : next;
    setEditedItems((prev) => ({ ...prev, [licenseId]: stateToSet }));
  };

  const includedItems = useMemo(
    () =>
      visibleItems.filter(
        (item) => editedItems[item.license_id]?.includedInPayment !== false,
      ),
    [visibleItems, editedItems],
  );

  const updateVisibleIncludedState = (included: boolean) => {
    setEditedItems((prev) => {
      const next = { ...prev };
      for (const item of visibleItems) {
        const existing = next[item.license_id];
        if (included) {
          const base =
            existing ?? buildInitialEditedItems([item])[item.license_id];
          const includeState = { ...base, includedInPayment: true };
          next[item.license_id] =
            itemTotalFromEdited(includeState) === 0
              ? { ...includeState, includedInPayment: false }
              : includeState;
        } else {
          next[item.license_id] = {
            ...(existing ?? buildInitialEditedItems([item])[item.license_id]),
            includedInPayment: false,
          };
        }
      }
      return next;
    });
  };

  const canInvoicePayIncludedSelection = useMemo(() => {
    if (!includedItems.length) return false;
    return includedItems.every(
      (item) => item.invoice_template_available === true,
    );
  }, [includedItems]);

  useEffect(() => {
    if (paymentMethod === "invoice" && !canInvoicePayIncludedSelection) {
      setPaymentMethod("card");
    }
  }, [paymentMethod, canInvoicePayIncludedSelection]);

  const buildPayload = (): {
    items: Array<{
      license_id: string;
      periods: Array<{
        selected: boolean;
        start_date: string;
        end_date: string | null;
        fee: string;
        late_fee: string;
        prefilled_fee: string;
        prefilled_late_fee: string;
      }>;
      inspection_fee?: string;
    }>;
    excluded_license_ids: string[];
  } => {
    if (!visibleItems.length) {
      return { items: [], excluded_license_ids: [] };
    }
    const excludedLicenseIds = visibleItems
      .filter(
        (item) => editedItems[item.license_id]?.includedInPayment === false,
      )
      .map((item) => item.license_id);
    const items = includedItems.map((item) => {
      const edited = editedItems[item.license_id];
      if (!edited)
        throw new Error(`Missing edited state for ${item.license_id}`);
      return {
        license_id: item.license_id,
        periods: item.fees.map((f, i) => {
          const p = edited.periods[i] ?? {
            selected: true,
            fee: f.registration_fee,
            late_fee: f.late_fee,
          };
          return {
            selected: p.selected,
            start_date: f.start_date,
            end_date: f.end_date,
            fee: p.selected ? roundToTwoDecimals(p.fee) : "0",
            late_fee: p.selected ? roundToTwoDecimals(p.late_fee) : "0",
            prefilled_fee: f.registration_fee,
            prefilled_late_fee: f.late_fee,
          };
        }),
        inspection_fee:
          edited.inspection_fee && Number(edited.inspection_fee) > 0
            ? roundToTwoDecimals(edited.inspection_fee)
            : undefined,
      };
    });
    return { items, excluded_license_ids: excludedLicenseIds };
  };

  const handlePay = async () => {
    if (submitLockRef.current) return;
    if (!visibleItems.length) return;
    if (includedItems.length === 0) {
      toast({
        title: "Error",
        description: "Select at least one property to pay.",
        variant: "destructive",
      });
      return;
    }
    if (
      paymentMethod === "invoice" &&
      amsOptions.length > 1 &&
      !effectiveAmsCompanyName
    ) {
      toast({
        title: "Error",
        description: "Select an AMS before creating an invoice.",
        variant: "destructive",
      });
      return;
    }
    submitLockRef.current = true;
    setIsSubmitting(true);
    setSubmitDisplayItems([...includedItems]);
    try {
      const { items, excluded_license_ids } = buildPayload();
      if (paymentMethod === "check") {
        const result = await createBulkCheckPayment({ items });
        await syncRegistrantDashboardCachesAfterBulkPayment(queryClient);
        setCheckPaymentSuccess({
          displayId: result.check_id,
          checkMailingAddress: result.check_mailing_address,
          amountToPay: formatCurrency(totalWithFee),
          variant: "check",
        });
        return;
      }
      if (paymentMethod === "invoice") {
        const result = await createBulkInvoicePayment({
          items,
          ams_company_name: effectiveAmsCompanyName,
        });
        await syncRegistrantDashboardCachesAfterBulkPayment(queryClient);
        setCheckPaymentSuccess({
          displayId: result.check_id,
          checkMailingAddress: null,
          amountToPay: formatCurrency(subtotal),
          variant: "invoice",
        });
        return;
      }
      await createBulkPendingPaymentRecords({ items, excluded_license_ids });
      const session = await makeBulkLicensePayment({
        payment_success_url: `${window.location.origin}/registrant/payment-success?sessionId={CHECKOUT_SESSION_ID}`,
        payment_cancel_url: `${window.location.origin}/registrant/payment-cancel?sessionId={CHECKOUT_SESSION_ID}&flow=bulk`,
        payment_method_type: paymentMethod,
        license_ids: items.map((item) => item.license_id),
      });
      if (session?.checkout_session_url) {
        window.location.href = session.checkout_session_url;
        return;
      }
      toast({
        title: "Error",
        description: "Failed to start payment",
        variant: "destructive",
      });
    } catch (e) {
      void invalidateRegistrantDashboardLists(queryClient);
      toast({
        title: "Error",
        description:
          e instanceof Error ? e.message : "Failed to process bulk payment",
        variant: "destructive",
      });
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
      setSubmitDisplayItems(null);
    }
  };

  if (!isAuthSetupComplete) {
    return <div className="p-4">Authenticating...</div>;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col container mx-auto max-w-3xl gap-4 p-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Please wait while we double check payments...
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col container mx-auto max-w-3xl gap-4 p-4">
        <p className="text-destructive">
          {error instanceof Error ? error.message : "Failed to load fees"}
        </p>
        <Link to="/registrant/dashboard">
          <Button>Back to Dashboard</Button>
        </Link>
      </div>
    );
  }

  if (data.items.length === 0 && !isSubmitting) {
    return (
      <div className="flex flex-col container mx-auto max-w-3xl gap-4 p-4">
        <PageHeader
          title="Fee Payment"
          subtitle={
            hasDashboardFilters
              ? "No properties with a balance due match your dashboard filters"
              : "No properties currently have a balance due"
          }
          action={<ContextHelpButton articleId="payments" />}
        />
        {hasDashboardFilters ? (
          <BulkPaymentDashboardFilterSummary
            filters={dashboardFilters}
            onClearFilters={handleClearDashboardFilters}
          />
        ) : null}
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">
              {hasDashboardFilters
                ? "Clear the filters above to see every property with a balance due."
                : "All your registrations are up to date. When you have properties with a balance due, they will appear here."}
            </p>
            <Link to="/registrant/dashboard">
              <Button className="mt-4">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col container mx-auto max-w-3xl gap-6 pb-8">
      <CheckPaymentSuccessDialog
        isOpen={!!checkPaymentSuccess}
        onOpenChange={(open) => {
          if (!open) setCheckPaymentSuccess(null);
        }}
        displayId={checkPaymentSuccess?.displayId ?? ""}
        checkMailingAddress={checkPaymentSuccess?.checkMailingAddress}
        amountToPay={checkPaymentSuccess?.amountToPay}
        variant={checkPaymentSuccess?.variant ?? "check"}
        onOk={() => {
          setCheckPaymentSuccess(null);
          navigate({ to: "/registrant/dashboard" });
        }}
      />
      <PageHeader
        title="Fee Payment"
        subtitle={
          showLicenseTypePicker
            ? "Review properties below, then pay."
            : "Review and adjust amounts for each property, then pay below"
        }
        action={
          <div className="flex flex-col items-end gap-3 shrink-0">
            <ContextHelpButton articleId="payments" />
            {showLicenseTypePicker ? (
              <div className="flex flex-col items-start gap-2">
                <RadioGroup
                  value={effectiveLicenseTypeId ?? undefined}
                  onValueChange={setSelectedLicenseTypeId}
                  className="flex flex-col gap-2 items-start"
                >
                  {licenseTypesWithDue.map((t) => (
                    <div key={t.id} className="flex items-center space-x-2">
                      <RadioGroupItem
                        value={t.id}
                        id={`bulk-license-type-${t.id}`}
                      />
                      <Label
                        htmlFor={`bulk-license-type-${t.id}`}
                        className="text-sm font-normal cursor-pointer whitespace-nowrap"
                      >
                        {t.name}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>
            ) : null}
          </div>
        }
      />

      {hasDashboardFilters ? (
        <BulkPaymentDashboardFilterSummary
          filters={dashboardFilters}
          onClearFilters={handleClearDashboardFilters}
        />
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        {showAmsPicker ? (
          <div className="w-full sm:max-w-sm space-y-1">
            <Label htmlFor="bulk-ams-select">Choose AMS</Label>
            <Select
              options={amsOptions.map((name) => ({
                value: name,
                label: name,
              }))}
              value={effectiveAmsCompanyName ? [effectiveAmsCompanyName] : []}
              onValueChange={(values) => {
                setSelectedAmsCompanyName(values[0] ?? null);
              }}
              multiple={false}
              placeholder="Select AMS"
              searchPlaceholder="Search AMS..."
              emptyMessage="No AMS found."
              buttonVariant="outline"
              className="border shadow-none bg-background"
            />
          </div>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => updateVisibleIncludedState(true)}
            disabled={visibleItems.length === 0}
          >
            Select All
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => updateVisibleIncludedState(false)}
            disabled={visibleItems.length === 0}
          >
            Select None
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {visibleItems.map((item) => (
          <PropertyPaymentCard
            key={item.license_id}
            item={item}
            edited={editedItems[item.license_id]}
            onUpdate={(next) => handleUpdateItem(item.license_id, next)}
            processingFeeDollars={processingFeeByLicenseId[item.license_id]}
            isIncludedInPayment={
              editedItems[item.license_id]?.includedInPayment !== false
            }
            onIncludeChange={(included) => {
              const ed = editedItems[item.license_id];
              if (!ed) return;
              if (included) {
                const initial = buildInitialEditedItems([item])[
                  item.license_id
                ];
                handleUpdateItem(item.license_id, {
                  ...initial,
                  includedInPayment: true,
                });
              } else {
                handleUpdateItem(item.license_id, {
                  ...ed,
                  includedInPayment: false,
                });
              }
            }}
          />
        ))}
      </div>

      <Separator />

      <div className="flex flex-col gap-4">
        <div className="p-4 border rounded-md bg-muted space-y-2">
          <div className="flex justify-between text-sm">
            <span>Registration / De-registration fee:</span>
            <span className="tabular-nums">
              {formatCurrency(totalRegistrationFee)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Late fee:</span>
            <span className="tabular-nums">{formatCurrency(totalLateFee)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Inspection fee:</span>
            <span className="tabular-nums">
              {formatCurrency(totalInspectionFee)}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span>Subtotal:</span>
            <span className="tabular-nums">{formatCurrency(subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm items-center gap-4">
            <span>Payment fee:</span>
            <div className="flex items-center gap-4">
              <RadioGroup
                value={paymentMethod}
                onValueChange={(v) =>
                  setPaymentMethod(
                    v as "card" | "us_bank_account" | "check" | "invoice",
                  )
                }
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="card" id="bulk-card" />
                  <Label
                    htmlFor="bulk-card"
                    className="text-xs font-normal cursor-pointer"
                  >
                    Credit Card
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="us_bank_account" id="bulk-ach" />
                  <Label
                    htmlFor="bulk-ach"
                    className="text-xs font-normal cursor-pointer"
                  >
                    ACH
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="check" id="bulk-check" />
                  <Label
                    htmlFor="bulk-check"
                    className="text-xs font-normal cursor-pointer"
                  >
                    Check
                  </Label>
                </div>
                {canInvoicePayIncludedSelection ? (
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="invoice" id="bulk-invoice" />
                    <Label
                      htmlFor="bulk-invoice"
                      className="text-xs font-normal cursor-pointer"
                    >
                      Pay by Invoice
                    </Label>
                  </div>
                ) : null}
              </RadioGroup>
              <span className="min-w-[60px] text-right tabular-nums">
                {formatCurrency(processingFeeDollars)}
              </span>
            </div>
          </div>
          <div className="flex justify-between font-medium pt-2 border-t">
            <span>Total:</span>
            <span className="tabular-nums">{formatCurrency(totalWithFee)}</span>
          </div>
        </div>

        {includedItems.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Select at least one property above to continue.
          </p>
        )}
        <Button
          size="lg"
          className="w-full sm:w-auto"
          onClick={handlePay}
          disabled={isSubmitting || includedItems.length === 0}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              {paymentMethod === "check" ? (
                <Banknote className="h-4 w-4 mr-2" />
              ) : paymentMethod === "us_bank_account" ? (
                <Landmark className="h-4 w-4 mr-2" />
              ) : paymentMethod === "invoice" ? (
                <FileText className="h-4 w-4 mr-2" />
              ) : (
                <CreditCard className="h-4 w-4 mr-2" />
              )}
              Pay by{" "}
              {paymentMethod === "check"
                ? "Check"
                : paymentMethod === "us_bank_account"
                  ? "ACH"
                  : paymentMethod === "invoice"
                    ? "Invoice"
                    : "Credit Card"}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function PropertyPaymentCard({
  item,
  edited,
  onUpdate,
  processingFeeDollars,
  isIncludedInPayment,
  onIncludeChange,
}: {
  item: BulkPaymentFeesItem;
  edited: EditedItemState | undefined;
  onUpdate: (next: EditedItemState) => void;
  processingFeeDollars?: number;
  isIncludedInPayment: boolean;
  onIncludeChange: (included: boolean) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const itemTotal = useMemo(() => {
    if (!edited) {
      const pt = item.fees.reduce(
        (sum, f) =>
          sum +
          Number.parseFloat(f.registration_fee) +
          Number.parseFloat(f.late_fee),
        0,
      );
      const insp = item.inspection_fee
        ? Number.parseFloat(item.inspection_fee)
        : 0;
      return pt + insp;
    }
    let total = 0;
    for (const p of edited.periods) {
      if (p.selected) {
        total += Number.parseFloat(p.fee) || 0;
        total += Number.parseFloat(p.late_fee) || 0;
      }
    }
    total += Number.parseFloat(edited.inspection_fee) || 0;
    return total;
  }, [item, edited]);

  const updatePeriod = (index: number, patch: Partial<EditedPeriod>) => {
    if (!edited) return;
    const nextPeriods = edited.periods.map((p, i) =>
      i === index ? { ...p, ...patch } : p,
    );
    onUpdate({ ...edited, periods: nextPeriods });
  };

  const setInspectionFee = (value: string) => {
    if (!edited) return;
    onUpdate({ ...edited, inspection_fee: value });
  };

  return (
    <Card
      className={`w-full min-w-0 overflow-hidden ${!isIncludedInPayment ? "opacity-75" : ""}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1 flex items-start gap-3">
            <div className="flex items-center pt-0.5 shrink-0">
              <Checkbox
                id={`${item.license_id}-include`}
                checked={isIncludedInPayment}
                onCheckedChange={(checked) =>
                  onIncludeChange(
                    checked === "indeterminate" ? true : !!checked,
                  )
                }
                aria-label="Include in payment"
                className="size-6"
              />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base">
                {item.property_address}
              </CardTitle>
              <CardDescription>
                {item.license_type_name} · {item.jurisdiction_name} (
                {item.display_id})
              </CardDescription>
              <FeesPaymentScreenQaBlock
                className="mt-0"
                questions={item.fees_payment_screen_questions ?? []}
                answers={item.answers_to_additional_questions}
              />
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {isIncludedInPayment ? (
              <span className="font-semibold tabular-nums">
                {formatCurrency(itemTotal)}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">Excluded</span>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsOpen((prev) => !prev)}
              aria-expanded={isOpen}
              aria-label={isOpen ? "Collapse details" : "Expand details"}
            >
              {isOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardHeader>
      {isOpen && (
        <CardContent className="w-full min-w-0 pt-3 space-y-4 border-t">
          <p className="text-xs text-muted-foreground">
            If you adjust the amounts below, any changes will be reviewed for
            compliance.
          </p>
          <div className="w-full min-w-0 space-y-4">
            {item.fees.map((fee, index) => {
              const periodEdited = edited?.periods[index];
              const isSelected = periodEdited?.selected ?? true;
              const checkboxId = `${item.license_id}-${fee.start_date}-selected`;
              return (
                <div
                  key={fee.start_date}
                  className="w-full min-w-0 space-y-3 border-b border-border pb-4 last:border-0 last:pb-0"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Checkbox
                      id={checkboxId}
                      checked={periodEdited?.selected ?? true}
                      onCheckedChange={(checked) =>
                        updatePeriod(index, {
                          selected:
                            checked === "indeterminate" ? true : !!checked,
                        })
                      }
                      disabled={!edited}
                    />
                    <Label htmlFor={checkboxId} className="text-sm font-normal">
                      {(() => {
                        const start = parseDateAsLocal(fee.start_date);
                        const end = fee.end_date
                          ? parseDateAsLocal(fee.end_date)
                          : null;
                        return start
                          ? `${format(start, "MMM d, yyyy")} - ${
                              end ? format(end, "MMM d, yyyy") : ""
                            }`
                          : fee.start_date;
                      })()}
                    </Label>
                    {(Number.parseFloat(fee.already_paid_fee ?? "0") || 0) >
                      0 && (
                      <p className="text-xs text-muted-foreground">
                        {formatCurrency(fee.already_paid_fee ?? "0")} previously
                        paid
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full min-w-0">
                    <div className="min-w-0 space-y-1">
                      <Label className="text-xs">Registration Fee</Label>
                      <FeeAmountInput
                        value={periodEdited?.fee ?? fee.registration_fee}
                        disabled={!edited || !isSelected}
                        onValueChange={(next) =>
                          updatePeriod(index, { fee: next })
                        }
                        className="w-full"
                      />
                    </div>
                    <div className="min-w-0 space-y-1">
                      <Label className="text-xs">Late Fee</Label>
                      <FeeAmountInput
                        value={periodEdited?.late_fee ?? fee.late_fee}
                        disabled={!edited || !isSelected}
                        onValueChange={(next) =>
                          updatePeriod(index, { late_fee: next })
                        }
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {item.inspection_fee != null && item.inspection_fee !== "" && (
            <div className="w-full min-w-0 space-y-2">
              <Label className="text-sm font-medium">Inspection Fee</Label>
              <FeeAmountInput
                value={edited?.inspection_fee ?? item.inspection_fee ?? "0"}
                disabled={!edited}
                onValueChange={setInspectionFee}
                className="w-full max-w-xs"
              />
            </div>
          )}

          <div className="w-full min-w-0 space-y-1 pt-2 border-t">
            {processingFeeDollars != null && processingFeeDollars > 0 && (
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Processing fee</span>
                <span className="tabular-nums">
                  {formatCurrency(processingFeeDollars)}
                </span>
              </div>
            )}
            <div className="flex justify-between font-medium">
              <span>Subtotal</span>
              <span className="tabular-nums">{formatCurrency(itemTotal)}</span>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
